import React, { useState, useEffect } from 'react';
import { ShoppingCart, Heart, Star, Plus, Package, Edit, Trash2, X, Upload, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_BASE_URL from '../services/api';
import { createMedia, updateMedia, uploadMediaFile } from '../services/media';

const API = `${API_BASE_URL}/api`;

const Shop = () => {
  const { user, token } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('availability');
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(['all']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Édition inline (fondateur)
  const emptyForm = { title: '', description: '', category: '', price: '', currency: 'EUR', stock_quantity: '', in_stock: true, featured: false, image_url: '', status: 'published' };
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const isFounder = user && user.user_type === 'fondateur';

  useEffect(() => {
    loadShopItems();
    loadCategories();
  }, []);

  const loadShopItems = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/media/items?media_type=shop_item&limit=50`);
      const data = response?.data;
      const items = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
      setProducts(items);
    } catch (err1) {
      // Fallback relative
      try {
        const res2 = await axios.get(`/api/media/items?media_type=shop_item&limit=50`);
        const d2 = res2?.data;
        const items2 = Array.isArray(d2) ? d2 : (Array.isArray(d2?.items) ? d2.items : []);
        setProducts(items2);
      } catch (err2) {
        console.error('Erreur lors du chargement de la boutique (fallback):', err2);
        setError('Erreur lors du chargement de la boutique');
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/media/categories`, { withCredentials: true });
      const categoryNames = response.data.map(cat => cat.name);
      setCategories(['all', ...categoryNames]);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  const handleBuyNow = async (itemId) => {
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/v2/checkout/shop`, { item_id: itemId, origin_url: origin }, { withCredentials: true });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error('Erreur paiement boutique:', e);
      setError(e.response?.data?.detail || 'Erreur lors de la création du paiement');
    }
  };

  const handleToggleSale = async (product) => {
    try {
      const nextInStock = !(product.in_stock === true);
      const nextStatus = nextInStock ? 'published' : 'draft';
      await axios.put(`${API}/media/items/${product.id}`, { in_stock: nextInStock, status: nextStatus }, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      });
      await loadShopItems();
    } catch (e) {
      console.error('Erreur bascule vente:', e);
      setError(e.response?.data?.detail || e.message);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Supprimer définitivement "${product.title}" de la boutique ?`)) return;
    try {
      await axios.delete(`${API}/media/items/${product.id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      });
      await loadShopItems();
    } catch (e) {
      console.error('Erreur suppression produit:', e);
      setError(e.response?.data?.detail || e.message);
    }
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); setError(''); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || '', description: p.description || '', category: p.category || '',
      price: p.price != null ? String(p.price) : '', currency: p.currency || 'EUR',
      stock_quantity: p.stock_quantity != null ? String(p.stock_quantity) : '',
      in_stock: p.in_stock !== false, featured: !!p.featured, image_url: p.image_url || '',
      status: p.status || 'published',
    });
    setModalOpen(true); setError('');
  };
  const closeModal = () => { setModalOpen(false); setEditingId(null); setForm(emptyForm); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadMediaFile(file, token);
      if (up?.url) setForm((f) => ({ ...f, image_url: up.url }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du téléversement de l\'image');
    } finally { setUploading(false); }
  };

  const handleSaveProduct = async () => {
    if (!form.title.trim()) { setError('Le nom du produit est requis'); return; }
    setSaving(true); setError('');
    const payload = {
      title: form.title.trim(),
      description: form.description || '',
      media_type: 'shop_item',
      status: form.status || 'published',
      image_url: form.image_url || null,
      category: form.category || null,
      featured: !!form.featured,
      price: form.price !== '' ? Number(form.price) : null,
      currency: form.currency || 'EUR',
      in_stock: !!form.in_stock,
      stock_quantity: form.stock_quantity !== '' ? Number(form.stock_quantity) : null,
    };
    try {
      if (editingId) await updateMedia(editingId, payload, token);
      else await createMedia(payload, token);
      closeModal();
      await loadShopItems();
      await loadCategories();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const filteredProducts = (onlyInStock ? products.filter(p => p.in_stock) : products)
    .filter(product => selectedCategory === 'all' ? true : product.category === selectedCategory);

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'availability':
        return (b.in_stock === true) - (a.in_stock === true);
      case 'price-asc':
        return (a.price || 0) - (b.price || 0);
      case 'price-desc':
        return (b.price || 0) - (a.price || 0);
      case 'name':
        return (a.title || '').localeCompare(b.title || '');
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  return (
    <div className="min-h-screen relative">
      <div className="mesh-bg" />
      <div className="grid-overlay" />
      <div className="container mx-auto px-4 py-8 relative">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-4">Boutique Solidaire</h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Achetez nos produits éthiques et soutenez nos missions humanitaires. 
                Tous les bénéfices sont reversés à nos projets de développement.
              </p>
            </div>
            {isFounder && (
              <div className="flex flex-col gap-2 ml-6">
                <Button onClick={openCreate} className="flex items-center gap-2" data-testid="shop-new-product-btn">
                  <Plus className="h-4 w-4" />
                  Nouveau Produit
                </Button>
              </div>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement des produits...</p>
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-24 w-24 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4">Aucun produit disponible</h3>
            <p className="text-muted-foreground mb-6">
              {isFounder 
                ? "Commencez par ajouter vos premiers produits à la boutique." 
                : "Les produits seront bientôt disponibles. Revenez nous voir !"}
            </p>
            {isFounder && (
              <Button 
                onClick={openCreate}
                className="flex items-center gap-2 mx-auto"
                data-testid="shop-add-first-product-btn"
              >
                <Plus className="h-4 w-4" />
                Ajouter le Premier Produit
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Filters and Sort */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              {categories.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={selectedCategory === 'all' ? "default" : "outline"}
                    onClick={() => setSelectedCategory('all')}
                    className="mb-2"
                  >
                    Tous les produits
                  </Button>
                  {categories.filter(cat => cat !== 'all').map((category) => (
                    <Button
                      key={category}
                      variant={selectedCategory === category ? "default" : "outline"}
                      onClick={() => setSelectedCategory(category)}
                      className="mb-2"
                    >
                      {category}
                    </Button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Trier par" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="availability">Disponibilité</SelectItem>
                    <SelectItem value="name">Nom A-Z</SelectItem>
                    <SelectItem value="price-asc">Prix croissant</SelectItem>
                    <SelectItem value="price-desc">Prix décroissant</SelectItem>
                    <SelectItem value="newest">Plus récent</SelectItem>
                  </SelectContent>
                </Select>
                <label className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={onlyInStock} onChange={e => setOnlyInStock(e.target.checked)} />
                  En stock uniquement
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sortedProducts.map((product) => (
                <Card key={product.id} className={`group glow-border hover:shadow-lg transition-all duration-300 cursor-pointer border-border/50 hover:border-primary/20 ${!product.in_stock ? 'opacity-70' : ''}`}>
                  {product.image_url && (
                    <div className="relative aspect-square overflow-hidden rounded-t-lg group">
                      <img 
                        src={product.image_url} 
                        alt={product.title}
                        className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="pointer-events-none absolute inset-0 transition-opacity duration-500 group-hover:bg-black/10 bg-black/5" />
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        {product.featured && (
                          <Badge className="bg-primary text-primary-foreground">Coup de ❤️</Badge>
                        )}
                        {!product.in_stock && (
                          <Badge variant="destructive" className="text-white">Rupture</Badge>
                        )}
                      </div>
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="font-semibold mb-1">{product.title}</div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {product.price != null ? `${product.price} ${product.currency || 'EUR'}` : '—'}
                      {product.category ? ` • ${product.category}` : ''}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => handleBuyNow(product.id)}>
                        <ShoppingCart className="h-4 w-4 mr-1"/> Acheter
                      </Button>
                      {isFounder && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openEdit(product)} className="flex items-center gap-1" data-testid={`shop-edit-${product.id}`}>
                            <Edit className="h-4 w-4"/> Modifier
                          </Button>
                          <Button size="sm" variant={product.in_stock ? 'destructive' : 'secondary'} onClick={() => handleToggleSale(product)}>
                            {product.in_stock ? 'Retirer de la vente' : 'Mettre en vente'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteProduct(product)} className="flex items-center gap-1 text-destructive hover:bg-destructive hover:text-white" data-testid={`shop-delete-${product.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {isFounder && modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="shop-product-modal" onClick={closeModal}>
          <div className="w-full max-w-lg max-h-[90vh] overflow-auto rounded-2xl border border-border bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h3 className="text-lg font-semibold">{editingId ? 'Modifier le produit' : 'Nouveau produit'}</h3>
              <Button variant="ghost" size="icon" onClick={closeModal} data-testid="shop-modal-close"><X className="h-4 w-4" /></Button>
            </div>
            <div className="p-5 space-y-4">
              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
              <div>
                <Label>Nom du produit *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: T-shirt PADAID" className="mt-1" data-testid="shop-form-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prix</Label>
                  <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" className="mt-1" data-testid="shop-form-price" />
                </div>
                <div>
                  <Label>Devise</Label>
                  <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} placeholder="EUR" className="mt-1" data-testid="shop-form-currency" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Catégorie</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex: Vêtements" className="mt-1" data-testid="shop-form-category" />
                </div>
                <div>
                  <Label>Stock (quantité)</Label>
                  <Input type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} placeholder="Optionnel" className="mt-1" data-testid="shop-form-stock" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Description du produit" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" data-testid="shop-form-description" />
              </div>
              <div>
                <Label>Image du produit</Label>
                <div className="mt-1 flex items-center gap-3">
                  {form.image_url && <img src={form.image_url} alt="aperçu" className="h-16 w-16 rounded-md object-cover border border-border" />}
                  <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-border px-3 py-2 text-sm hover:bg-muted" data-testid="shop-form-image-label">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? 'Téléversement…' : (form.image_url ? 'Changer l\'image' : 'Téléverser une image')}
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="shop-form-image-input" />
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} className="accent-primary" data-testid="shop-form-instock" />
                  Disponible à la vente
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-primary" data-testid="shop-form-featured" />
                  Coup de cœur
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.status === 'published'} onChange={(e) => setForm({ ...form, status: e.target.checked ? 'published' : 'draft' })} className="accent-primary" data-testid="shop-form-published" />
                  Publié
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
              <Button variant="outline" onClick={closeModal} data-testid="shop-modal-cancel">Annuler</Button>
              <Button onClick={handleSaveProduct} disabled={saving || uploading} className="bg-primary hover:bg-primary/90" data-testid="shop-modal-save">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement…</> : (editingId ? 'Enregistrer' : 'Créer le produit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;